import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { newsCopy } from "@/data/news";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "./Home";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso, lang) => {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const useNews = () => {
  const [articles, setArticles] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/news`);
        if (res.ok) setArticles(await res.json());
        else setArticles([]);
      } catch {
        setArticles([]);
      }
    })();
  }, []);
  return articles;
};

export const NewsPreview = () => {
  const { lang } = useLanguage();
  const t = newsCopy[lang];
  const articles = useNews();
  if (!articles || articles.length === 0) return null;
  const latest = articles.slice(0, 3);
  return (
    <section className="py-24 md:py-32 px-6 lg:px-12" data-testid="home-news-section">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="flex items-end justify-between gap-6 mb-14">
            <div>
              <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-4">{t.homeKicker}</p>
              <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight">{t.homeTitle}</h2>
            </div>
            <Link
              to="/news"
              data-testid="home-news-all"
              className="hidden sm:inline-flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-red-500 transition-colors shrink-0"
            >
              {t.homeCta}
              <ArrowRight size={13} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latest.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08}>
              <Link
                to={`/news/${a.id}`}
                data-testid={`home-news-card-${i}`}
                className="group block bg-[#101114] border border-white/10 rounded-lg overflow-hidden hover:border-red-600/40 transition-colors duration-500 h-full"
              >
                {a.image && (
                  <div className="aspect-video overflow-hidden">
                    <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-6">
                  <p className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{fmtDate(a.date, lang)}</p>
                  <h3 className="mt-3 f-display text-lg font-bold tracking-tight leading-snug group-hover:text-red-500 transition-colors duration-300">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-2">{a.description}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function News() {
  const { lang } = useLanguage();
  const t = newsCopy[lang];
  const articles = useNews();

  return (
    <main className="relative px-6 lg:px-12 pt-20 md:pt-28 pb-28 overflow-hidden" data-testid="news-page">
      <Seo title="RV FILM — News & Blog" description={t.intro} />
      <div className="absolute -right-40 -top-20 w-[480px] h-[480px] spotlight-red" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto">
        <Reveal y={12}>
          <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">{t.kicker}</p>
          <h1 className="f-display font-black tracking-tight leading-[0.95] text-[11vw] sm:text-6xl lg:text-7xl" data-testid="news-title">
            {t.titleA}
            <span className="f-serif italic font-normal text-red-500 block">{t.titleB}</span>
          </h1>
          <p className="mt-8 text-slate-400 text-base sm:text-lg max-w-2xl leading-relaxed">{t.intro}</p>
        </Reveal>

        <div className="mt-16">
          {articles === null ? (
            <p className="f-mono text-xs tracking-[0.25em] uppercase text-slate-500">{t.loading}</p>
          ) : articles.length === 0 ? (
            <p className="text-slate-500" data-testid="news-empty">{t.empty}</p>
          ) : (
            <div className="border-t border-white/10">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={Math.min(i * 0.05, 0.3)}>
                  <Link
                    to={`/news/${a.id}`}
                    data-testid={`news-card-${i}`}
                    className="group grid grid-cols-1 md:grid-cols-12 gap-6 py-8 border-b border-white/10 items-center"
                  >
                    {a.image && (
                      <div className="md:col-span-3 rounded-lg overflow-hidden border border-white/10">
                        <div className="aspect-video overflow-hidden">
                          <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        </div>
                      </div>
                    )}
                    <div className={a.image ? "md:col-span-8" : "md:col-span-11"}>
                      <p className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{fmtDate(a.date, lang)}</p>
                      <h2 className="mt-2 f-display text-2xl md:text-3xl font-bold tracking-tight group-hover:text-red-500 transition-colors duration-300">
                        {a.title}
                      </h2>
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-2xl">{a.description}</p>
                    </div>
                    <div className="md:col-span-1 flex md:justify-end">
                      <ArrowUpRight size={22} className="text-slate-600 group-hover:text-red-500 transition-colors duration-300" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export function NewsArticle() {
  const { id } = useParams();
  const { lang } = useLanguage();
  const t = newsCopy[lang];
  const articles = useNews();
  const article = articles?.find((a) => a.id === id);

  if (articles === null) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center">
        <p className="f-mono text-xs tracking-[0.25em] uppercase text-slate-500">{t.loading}</p>
      </main>
    );
  }
  if (!article) {
    return (
      <main className="min-h-[60vh] flex flex-col items-center justify-center gap-6 px-6">
        <p className="text-slate-500">{t.empty}</p>
        <Link to="/news" className="f-mono text-[10px] uppercase tracking-[0.2em] text-red-500">{t.back}</Link>
      </main>
    );
  }

  return (
    <main data-testid="news-article-page">
      <Seo title={`RV FILM — ${article.title}`} description={article.description} />
      <article className="px-6 lg:px-12 pt-20 md:pt-28 pb-16">
        <div className="max-w-3xl mx-auto">
          <Reveal y={12}>
            <Link to="/news" data-testid="news-back-link" className="inline-flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-slate-200 transition-colors mb-10">
              <ArrowLeft size={13} />
              {t.back}
            </Link>
            <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-5">{fmtDate(article.date, lang)}</p>
            <h1 className="f-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight" data-testid="news-article-title">
              {article.title}
            </h1>
          </Reveal>
          {article.image && (
            <Reveal delay={0.15}>
              <div className="mt-10 rounded-lg overflow-hidden border border-white/10">
                <img src={article.image} alt={article.title} className="w-full h-auto" />
              </div>
            </Reveal>
          )}
          <Reveal delay={0.25}>
            <div className="article-body mt-10" dangerouslySetInnerHTML={{ __html: article.content }} data-testid="news-article-body" />
          </Reveal>
          {article.link && (
            <Reveal delay={0.1}>
              <a
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="news-source-link"
                className="mt-10 inline-flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-red-500 transition-colors"
              >
                {t.source}
                <ArrowUpRight size={13} />
              </a>
            </Reveal>
          )}
        </div>
      </article>
      <CtaBand />
    </main>
  );
}
